import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { SvgIconComponent } from 'angular-svg-icon';
import {
  AccessibilityService,
  ContrastMode,
} from '../../core/services/accessibility.service';

@Component({
  selector: 'app-accessibility-panel',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzToolTipModule,
    NzDropDownModule,
    SvgIconComponent,
  ],
  templateUrl: './accessibility-panel.component.html',
  styleUrls: ['./accessibility-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessibilityPanelComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('a11yDialog') a11yDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('a11yTrigger') a11yTrigger!: ElementRef<HTMLDivElement>;
  private readonly a11yService = inject(AccessibilityService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly el = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();

  private readonly BOTTOM_SNAP_THRESHOLD = 100;
  private readonly BTN_SIZE = 44;

  fontScalePercent = 100;
  contrastMode: ContrastMode = 'default';
  isPanelOpen = false;
  isDragging = false;
  isDefaultPosition = true;
  triggerStyle: Record<string, string> = {};
  dialogStyle: Record<string, string> = {};

  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private hasDragged = false;

  ngOnInit(): void {
    this.a11yService.fontScale$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.fontScalePercent = this.a11yService.getFontScalePercent();
      this.cdr.markForCheck();
    });

    this.a11yService.contrastMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        this.contrastMode = mode;
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    this.a11yDialog.nativeElement.addEventListener('close', () => {
      this.isPanelOpen = false;
      this.cdr.markForCheck();
    });

    // Add touchstart with passive:false so we can call preventDefault during drag
    this.a11yTrigger.nativeElement.addEventListener(
      'touchstart',
      (e: TouchEvent) => this.onDragStart(e),
      { passive: false }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDragStart(event: MouseEvent | TouchEvent): void {
    if (event instanceof MouseEvent && event.button !== 0) return;
    event.preventDefault();

    const point =
      event instanceof MouseEvent
        ? { x: event.clientX, y: event.clientY }
        : { x: event.touches[0].clientX, y: event.touches[0].clientY };

    const rect = this.a11yTrigger.nativeElement.getBoundingClientRect();
    this.dragOffsetX = point.x - rect.left;
    this.dragOffsetY = point.y - rect.top;
    this.isDragging = true;
    this.hasDragged = false;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.performDrag(event.clientX, event.clientY);
  }

  @HostListener('document:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    this.performDrag(event.touches[0].clientX, event.touches[0].clientY);
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onDragEnd(): void {
    if (!this.isDragging) return;
    this.isDragging = false;

    if (this.hasDragged) {
      const rect = this.a11yTrigger.nativeElement.getBoundingClientRect();
      const nearBottom =
        rect.bottom >= window.innerHeight - this.BOTTOM_SNAP_THRESHOLD;

      if (nearBottom) {
        this.snapToDefault();
      } else {
        this.updateDialogStyle();
      }
      this.cdr.markForCheck();
    }
  }

  onToggleClick(): void {
    if (this.hasDragged) {
      this.hasDragged = false;
      return;
    }
    this.togglePanel();
  }

  togglePanel(): void {
    const dialog = this.a11yDialog.nativeElement;
    if (this.isPanelOpen) {
      dialog.close();
      this.isPanelOpen = false;
    } else {
      dialog.show();
      this.isPanelOpen = true;
      if (!this.isDefaultPosition) {
        this.updateDialogStyle();
      }
    }
    this.cdr.markForCheck();
  }

  increaseFontSize(): void {
    this.a11yService.increaseFontScale();
  }

  decreaseFontSize(): void {
    this.a11yService.decreaseFontScale();
  }

  resetFontSize(): void {
    this.a11yService.resetFontScale();
  }

  toggleContrast(): void {
    this.a11yService.toggleContrastMode();
  }

  getContrastLabel(): string {
    switch (this.contrastMode) {
      case 'high-contrast':
        return 'High Contrast';
      case 'dark':
        return 'Dark';
      default:
        return 'Default';
    }
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent): void {
    if (this.isPanelOpen && !this.el.nativeElement.contains(event.target)) {
      this.a11yDialog.nativeElement.close();
      this.isPanelOpen = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.shiftKey) {
      switch (event.key) {
        case '+':
        case '=':
          event.preventDefault();
          this.increaseFontSize();
          break;
        case '-':
        case '_':
          event.preventDefault();
          this.decreaseFontSize();
          break;
        case '0':
          event.preventDefault();
          this.resetFontSize();
          break;
      }
    }
  }

  private performDrag(clientX: number, clientY: number): void {
    const maxX = window.innerWidth - this.BTN_SIZE;
    const maxY = window.innerHeight - this.BTN_SIZE;
    const newLeft = Math.max(0, Math.min(clientX - this.dragOffsetX, maxX));
    const newTop = Math.max(0, Math.min(clientY - this.dragOffsetY, maxY));

    this.triggerStyle = {
      left: `${newLeft}px`,
      top: `${newTop}px`,
      right: 'auto',
      bottom: 'auto',
    };
    this.isDefaultPosition = false;
    this.hasDragged = true;
    this.cdr.markForCheck();
  }

  private snapToDefault(): void {
    this.triggerStyle = {};
    this.dialogStyle = {};
    this.isDefaultPosition = true;
  }

  private updateDialogStyle(): void {
    if (this.isDefaultPosition) {
      this.dialogStyle = {};
      return;
    }

    const rect = this.a11yTrigger.nativeElement.getBoundingClientRect();
    const dialogWidth = 260;
    const dialogHeight = 220;
    const gap = 8;

    // Prefer placing dialog above the button; fall back to below if not enough room
    const top =
      rect.top > dialogHeight + gap
        ? rect.top - dialogHeight - gap
        : rect.bottom + gap;

    // Align dialog's right edge with button's right edge, clamped to viewport
    const left = Math.max(
      8,
      Math.min(rect.right - dialogWidth, window.innerWidth - dialogWidth - 8)
    );

    this.dialogStyle = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      bottom: 'auto',
      right: 'auto',
    };
  }
}
